if(!customElements.get('flex-slideshow')) {
  class FlexSlideshow extends HTMLElement {
    constructor(){
      super();
      // DOM refs
      this._track = this.querySelector('.carousel__track');
      this._btnPrev = this.querySelector('.carousel__btn--prev');
      this._btnNext = this.querySelector('.carousel__btn--next');

      // Slides/state
      this._realSlides = [];
      this._realCount = 0;

      // Looping
      this._isInfinite = this.hasAttribute('infinite'); // attribute intent
      this._loopActive = this._isInfinite;              // runtime loop flag (disabled on re-init)
      this._CLONE_COUNT = parseInt(this.dataset.cloneCount) ?? 0;
      this._index = this._loopActive ? this._CLONE_COUNT : 0;
      this._maxIndex = 0;

      // Layout
      this._perView = 1;
      this._gapPx = 0;
      this._slideStep = 0;
      this._translateX = 0;

      // Centering / visuals
      this._scaleCenter = this.hasAttribute('scale-center');
      this._updateBackground = this.hasAttribute('update-background');
      this._updateColour = this.hasAttribute('update-colour');

      // Toggle for centered-first behavior (bounded mode only)
      this.centeringEnabled = this.hasAttribute('center') ?? false;
      this._didCenterFirst = false;
      this._baseOffsetPx = 0;  // positive px to center first slide in bounded mode

      // Drag/momentum
      this._isDragging = false;
      this._isAnimating = false;
      this._startX = 0;
      this._startTranslateX = 0;
      this._lastPointerX = 0;
      this._velocity = 0;     // px/ms
      this._lastTs = 0;

      // Drag threshold / activation
      this._DRAG_THRESHOLD = parseInt(this.getAttribute('drag-threshold')) || 50;
      this._dragActivated = false;
      this._desktopDragListening = false;

      // Click suppression (prevents ghost clicks after dragging)
      this._movedForClickCancel = false;
      this._suppressNextClick = false;

      // Autoplay
      this._isAutoplay = this._getBoolAttr('autoplay');
      this.autoplaySpeed = parseInt(this.getAttribute('autoplay-speed')) || 3000;
      this._autoplayTimer = null;

      // Reduced motion detection
      this._prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this._reducedMotionMql = window.matchMedia('(prefers-reduced-motion: reduce)');

      // Bindings
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onTouchCancel = this._onTouchCancel.bind(this);
      this._measure = this._measure.bind(this);
      this._goPrev = this._goPrev.bind(this);
      this._goNext = this._goNext.bind(this);
      this._updateCenterHighlightFromTranslate = this._updateCenterHighlightFromTranslate.bind(this);

      this.initialise = this.initialise.bind(this);
      this._onFilterApplied = this._onFilterApplied.bind(this);

      this._hasInitialisedOnce = false;
      this._mqlMobile = window.matchMedia('(max-width: 767px)');
    }

    static get observedAttributes(){ return ['per-view', 'infinite', 'autoplay', 'drag-threshold']; }

    attributeChangedCallback(name){
      if (name === 'per-view') this._measure();
      if (name === 'infinite') {
        this._isInfinite = this.hasAttribute('infinite'); // runtime loop still controlled internally
      }
      if (name === 'autoplay') {
        this._isAutoplay = this._getBoolAttr('autoplay');
        if (this._isAutoplay) this._startAutoplay();
        else this._stopAutoplay();
      }
      if (name === 'drag-threshold') {
        this._DRAG_THRESHOLD = parseInt(this.getAttribute('drag-threshold')) || 50;
      }
    }

    connectedCallback(){
      this._bindEvents();
      this.initialise();

      // Listen for reduced motion preference changes
      this._onReducedMotionChange = (e) => {
        this._prefersReducedMotion = e.matches;
        if (this._prefersReducedMotion && this._autoplayTimer) {
          this._stopAutoplay();
        }
      };
      this._reducedMotionMql.addEventListener('change', this._onReducedMotionChange);

      // Re-initialise (with loop disabled) whenever filtering changes visibility.
      document.addEventListener('filter:applied', this._onFilterApplied);

      // Prevents anchor re-directions after dragging
      this.addEventListener('click', e => {
        if (this._suppressNextClick) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this._suppressNextClick = false; // one-shot
        }
      }, true);
    }

    disconnectedCallback(){
      this._track?.removeEventListener('mousedown', this._onPointerDown);

      // Touch listeners
      this.removeEventListener('touchmove', this._onPointerMove);
      this.removeEventListener('touchend', this._onPointerUp);
      this.removeEventListener('touchcancel', this._onTouchCancel);

      // In case a desktop drag was in progress
      if (this._desktopDragListening) {
        window.removeEventListener('mousemove', this._onPointerMove);
        window.removeEventListener('mouseup', this._onPointerUp);
        this._desktopDragListening = false;
      }

      this._btnPrev?.removeEventListener('click', this._goPrev);
      this._btnNext?.removeEventListener('click', this._goNext);

      this._ro?.disconnect();
      this._mqlMobile.removeEventListener?.('change', this._measure);
      window.removeEventListener('orientationchange', this._measure);

      // Clean up reduced motion listener
      this._reducedMotionMql?.removeEventListener('change', this._onReducedMotionChange);

      document.removeEventListener('filter:applied', this._onFilterApplied);

      this._stopAutoplay();
    }

    // ===== Visibility/clone helpers =====
    _isVisible(el){
      if (el.hasAttribute('is-clone')) return false;
      const cs = getComputedStyle(el);
      return cs.display !== 'none';
    }
    _purgeClones(){
      this._track?.querySelectorAll('[is-clone]').forEach(n => n.remove());
    }
    _collectVisibleRealSlides(){
      this._realSlides = Array.from(this._track?.children || []).filter(el => this._isVisible(el));
      this._realCount = this._realSlides.length;
    }
    _cloneSlides(){
      if (!this._loopActive || this._realCount === 0 || this._CLONE_COUNT === 0) return;

      // Tail clones
      for (let i = 0; i < this._CLONE_COUNT; i++) {
        const clone = this._realSlides[i % this._realCount].cloneNode(true);
        clone.setAttribute('tabindex', '-1');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('is-clone', 'true');
        this._track.appendChild(clone);
      }
      // Head clones
      for (let i = 0; i < this._CLONE_COUNT; i++) {
        const src = this._realSlides[(this._realCount - this._CLONE_COUNT + i + this._realCount) % this._realCount];
        const clone = src.cloneNode(true);
        clone.setAttribute('tabindex', '-1');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('is-clone', 'true');
        this._track.insertBefore(clone, this._track.firstChild);
      }
    }

    // Public: (re)calculate layout based on *visible* slides only.
    initialise(){
      if (!this._track) return;

      // Remove any clones first, so counts/indices are based on visible real slides only
      this._purgeClones();

      // Refresh visible real slides
      this._collectVisibleRealSlides();

      // Decide loop mode
      if (!this._hasInitialisedOnce) {
        this._loopActive = this._isInfinite && this._realCount > 0;
      } else {
        // Re-init path: disable looping per request
        this._loopActive = false;
      }

      // Rebuild clones if loop is active
      if (this._loopActive) {
        this._cloneSlides();
        this._index = Math.max(this._CLONE_COUNT, this._index);
      } else {
        this._index = 0;
      }

      // Measure and lay out
      this._measure();

      // One-time center-first (only if enabled)
      this._centerFirstOnInit();

      this._applyTranslate(true);
      this._updateCenterHighlightFromTranslate();
      this._updateNavState();

      // Autoplay (re-read current attribute each time)
      this._isAutoplay = this._getBoolAttr('autoplay');
      // Disable autoplay if reduced motion is preferred
      if (this._isAutoplay && !this._prefersReducedMotion) {
        this._stopAutoplay();
        this._startAutoplay();
      } else {
        this._stopAutoplay();
      }

      this._hasInitialisedOnce = true;
    }

    _onFilterApplied(){
      // After filtering, re-init WITHOUT loop; clones stay removed.
      this.initialise();
    }

    _bindEvents(){
      if (!this._track) {
        console.error('[flex-slideshow] No track element found! Cannot bind events.');
        return;
      }

      // Mouse: only listen for mousedown here; we attach move/up on window during an active mouse interaction
      this._track.addEventListener('mousedown', this._onPointerDown);

      // Touch: listen on component; pointer-up/cancel will also be on component
      this.addEventListener('touchmove', this._onPointerMove, { passive: false });
      this.addEventListener('touchend', this._onPointerUp, { passive: true });
      this.addEventListener('touchcancel', this._onTouchCancel, { passive: true });
      this._track.addEventListener('touchstart', this._onPointerDown, { passive: true });

      this._btnPrev?.addEventListener('click', this._goPrev);
      this._btnNext?.addEventListener('click', this._goNext);

      this._ro = new ResizeObserver(this._measure);
      this._ro.observe(this);
      this._mqlMobile.addEventListener?.('change', this._measure);
      window.addEventListener('orientationchange', this._measure);
    }

    // ===== Autoplay =====
    _startAutoplay(){
      // Don't start autoplay if reduced motion is preferred
      if (this._prefersReducedMotion) return;
      
      this._stopAutoplay();
      this._autoplayTimer = setInterval(() => {
        if (!this._isDragging && !this._isAnimating && this._realCount > 0) {
          this._snapToIndex(this._index + 1, true);
        }
      }, this.autoplaySpeed);
    }
    _stopAutoplay(){
      if (this._autoplayTimer){
        clearInterval(this._autoplayTimer);
        this._autoplayTimer = null;
      }
    }

    // ===== Layout/measure =====
    _computePerView(){
      const requested = this._clamp(parseInt(this.getAttribute('per-view') || '3', 10) || 3, 1, 5);
      return this._mqlMobile.matches ? 1 : requested;
    }
    _syncCSSVars(){
      this._gapPx = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--gap')) || 0;
    }

    _measure(){
      if (!this.isConnected) return;
      if (this._isAnimating) return; // avoid reflow/step changes mid-transition

      this._perView = this._computePerView();
      this._syncCSSVars();

      const viewport = this.clientWidth;
      
      // Mobile fix: Read actual slide width from DOM instead of calculating
      // This accounts for the real rendered size including gaps
      if (this._perView === 1 && this._realSlides.length > 0) {
        const firstSlide = this._realSlides[0];
        const slideWidth = firstSlide.offsetWidth;
        this._slideStep = slideWidth + this._gapPx;
      } else {
        const visibleGaps = Math.max(0, this._perView - 1) * this._gapPx;
        const widthPerSlide = (viewport - visibleGaps) / Math.max(1, this._perView);
        this._slideStep = widthPerSlide + this._gapPx;
      }

      // recompute base offset (bounded center-first) — only when centeringEnabled
      this._baseOffsetPx =
        (!this._loopActive && this._perView > 1 && this.centeringEnabled)
          ? Math.floor(this._perView / 2) * this._slideStep
          : 0;

      if (this._realCount === 0){
        this._index = 0;
        this._setTranslateX(0, true);
        this._btnPrev && (this._btnPrev.disabled = true);
        this._btnNext && (this._btnNext.disabled = true);
        return;
      }

      if (this._loopActive) {
        const middleOffset = Math.floor(this._perView / 2);
        const minIndex = this._didCenterFirst ? Math.max(0, this._CLONE_COUNT - middleOffset) : this._CLONE_COUNT;
        const lastRealStart = this._CLONE_COUNT + this._realCount - 1;

        if (this._index < minIndex) this._index = minIndex;
        if (this._index > lastRealStart) this._index = lastRealStart;

        this._applyTranslate(true);
        this._btnPrev && (this._btnPrev.disabled = false);
        this._btnNext && (this._btnNext.disabled = false);
      } else {
        this._maxIndex = Math.max(0, this._realCount - this._perView);
        this._index = this._clamp(this._index, 0, this._maxIndex);
        this._applyTranslate(true);
        this._btnPrev && (this._btnPrev.disabled = this._index <= 0 && this._baseOffsetPx === 0);
        this._btnNext && (this._btnNext.disabled = this._index >= this._maxIndex);
      }

      this._updateCenterHighlightFromTranslate();
    }

    _centerFirstOnInit(){
      if (this._didCenterFirst) return;
      const middleOffset = Math.floor(this._perView / 2);

      if (this.centeringEnabled && middleOffset > 0) {
        if (this._loopActive) {
          this._index = Math.max(0, this._CLONE_COUNT - middleOffset);
        } // bounded mode centers via _baseOffsetPx (handled in _measure)
      }
      this._didCenterFirst = true;
    }

    // ===== Movement helpers =====
    _roundPx(x){ return Math.round(x * 1000) / 1000; }

    _applyTranslate(noTransition = false){
      const offset = this._loopActive ? 0 : this._baseOffsetPx;
      const x = -this._index * this._slideStep + offset;
      this._setTranslateX(x, noTransition);
    }

    _setTranslateX(x, noTransition = false){
      this._translateX = this._roundPx(x);
      if (noTransition) this._track.classList.add('no-transition');
      this._track.style.transform = `translate3d(${this._translateX}px, 0px, 0px)`;
      if (noTransition){
        this._track.getBoundingClientRect(); // reflow
        this._track.classList.remove('no-transition');
      }
    }

    _freezeSlideTransitions(freeze){
      const kids = this._track?.children || [];
      for (const el of kids){
        if (freeze){
          if (!el.dataset._prevTransition) el.dataset._prevTransition = el.style.transition || '';
          el.style.transition = 'none';
        } else {
          if ('_prevTransition' in el.dataset){
            el.style.transition = el.dataset._prevTransition;
            delete el.dataset._prevTransition;
          } else {
            el.style.transition = '';
          }
        }
      }
    }

    _normalizeIndexIfNeeded(){
      if (!this._loopActive) return;

      const firstReal = this._CLONE_COUNT;
      const lastRealStart = this._CLONE_COUNT + this._realCount - 1;

      if (this._index < firstReal || this._index > lastRealStart){
        this.classList.add('is-normalizing');
        this._freezeSlideTransitions(true);

        if (this._index < firstReal){
          this._index += this._realCount;
        } else {
          this._index -= this._realCount;
        }
        this._applyTranslate(true);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this._freezeSlideTransitions(false);
            this.classList.remove('is-normalizing');
            this._updateCenterHighlightFromTranslate();
          });
        });
      }
    }

    _updateNavState(){
      if (this._realCount === 0){
        this._btnPrev && (this._btnPrev.disabled = true);
        this._btnNext && (this._btnNext.disabled = true);
        return;
      }
      if (this._loopActive) {
        this._btnPrev && (this._btnPrev.disabled = false);
        this._btnNext && (this._btnNext.disabled = false);
      } else {
        const atStart = (this._index <= 0) && (this._baseOffsetPx === 0);
        const atEnd = this._index >= this._maxIndex;
        this._btnPrev && (this._btnPrev.disabled = atStart);
        this._btnNext && (this._btnNext.disabled = atEnd);
      }
    }

    _snapToIndex(i, withTransition = true){
      if (this._realCount === 0) return;

      const clampedIndex = this._loopActive ? i : this._clamp(i, 0, this._maxIndex);

      const targetX = this._roundPx(
        -clampedIndex * this._slideStep + (this._loopActive ? 0 : this.centeringEnabled ? this._baseOffsetPx : 0)
      );
      const currentX = this._translateX;
      const epsilon = 0.5;
      const isNoop = Math.abs(targetX - currentX) < epsilon;

      const doTransition = withTransition && !isNoop;
      if (this._isAnimating && doTransition) return;
      this._isAnimating = !!doTransition;

      this._index = clampedIndex;
      this._setTranslateX(targetX, false);
      if (doTransition) this._track.classList.remove('dragging');

      const after = () => {
        this._isAnimating = false;
        this._normalizeIndexIfNeeded();
        this._updateCenterHighlightFromTranslate();
        this._updateNavState();
      };

      if (doTransition){
        const onDone = () => {
          this._track.removeEventListener('transitionend', onDone);
          after();
        };
        this._track.addEventListener('transitionend', onDone, { once: true });
      } else {
        after();
      }
    }

    _snapToNearest(){
      if (this._realCount === 0) return;

      if (this._loopActive) {
        const raw = Math.round(-this._translateX / this._slideStep);
        this._snapToIndex(raw, true);
        return;
      }

      // Bounded mode
      const step = this._slideStep;
      const offset = this._baseOffsetPx; // will be 0 if centering is disabled

      // Logical centered index from current X
      const rawCentered = (-this._translateX + offset) / step;
      const nearestIdx = this._clamp(Math.round(rawCentered), 0, this._maxIndex);
      const xCentered = -nearestIdx * step + offset;

      // Left-aligned first slide (X = 0) special case
      const xLeftAlignedFirst = 0;

      // Choose closer target
      const distCentered = Math.abs(this._translateX - xCentered);
      const distLeft = Math.abs(this._translateX - xLeftAlignedFirst);

      if (distLeft < distCentered) {
        this._animateToX(xLeftAlignedFirst, () => {
          this._index = 0;
          this._updateCenterHighlightFromTranslate();
          this._updateNavState();
        });
      } else {
        this._snapToIndex(nearestIdx, true);
      }
    }

    _animateToX(targetX, after){
      const currentX = this._translateX;
      const epsilon = 0.5;
      const isNoop = Math.abs(targetX - currentX) < epsilon;
      const doTransition = !isNoop;

      if (this._isAnimating && doTransition) return;
      this._isAnimating = !!doTransition;

      this._setTranslateX(targetX, false);

      const finish = () => {
        this._isAnimating = false;
        after && after();
      };

      if (doTransition){
        const onDone = () => {
          this._track.removeEventListener('transitionend', onDone);
          finish();
        };
        this._track.addEventListener('transitionend', onDone, { once: true });
      } else {
        finish();
      }
    }

    // ===== Center highlight =====
    _updateCenterHighlightFromTranslate(){
      if (this._realCount === 0) return;

      let centerChildIndex;

      if (this._loopActive || this._baseOffsetPx === 0) {
        const firstVisible = this._loopActive
          ? Math.round(-this._translateX / this._slideStep)
          : this._clamp(Math.round(-this._translateX / this._slideStep), 0, this._maxIndex);

        const middleOffset = Math.floor(this._perView / 2);
        centerChildIndex = firstVisible + middleOffset;
      } else {
        // Bounded center-first when enabled
        const logicalCentered = Math.round((-this._translateX + this._baseOffsetPx) / this._slideStep);
        centerChildIndex = this._clamp(logicalCentered, 0, this._track.children.length - 1);
      }

      const count = this._track.children.length;
      centerChildIndex = this._clamp(centerChildIndex, 0, count - 1);

      for (const el of this._track.children) el.classList.remove('is-center');
      const centerEl = this._track.children[centerChildIndex];
      if (centerEl) centerEl.classList.add('is-center');

      if (centerEl && this._updateBackground) {
        const bg = centerEl?.dataset?.background;
        this.style.setProperty('--slideshow-background', bg ? bg : '');
      }
      if (centerEl && this._updateColour) {
        const color = centerEl?.dataset?.text;
        this.style.setProperty('--slideshow-color', color ? color : '');
        if (color) {
          this.querySelectorAll('.button-block')?.forEach(btn => {
            btn.style.setProperty('--button-text-color', color);
            btn.style.setProperty('--button-border-color', color);
          });
        }
      }
    }

    // ===== Pointer handlers (with activation threshold) =====
    _onPointerDown(e){
      if (e.touches && e.touches.length > 1) return;
      if (this._isAnimating) return;
      if (this._realCount === 0) return;

      const pointX = this._getPointX(e);
      this._isDragging = false;
      this._dragActivated = false;

      this._startX = pointX;
      this._lastPointerX = pointX;
      this._lastTs = performance.now();
      this._startTranslateX = this._translateX;
      this._velocity = 0;

      // click suppression flags reset
      this._movedForClickCancel = false;
      this._suppressNextClick = false;

      // For desktop: attach global listeners so mouseup outside ends drag
      if (!this._isTouchEvt(e) && !this._desktopDragListening) {
        window.addEventListener('mousemove', this._onPointerMove);
        window.addEventListener('mouseup', this._onPointerUp);
        this._desktopDragListening = true;
      }
    }

    _onPointerMove(e){
      // Touch: only prevent default once drag is activated
      if (this._isTouchEvt(e)) {
        if (this._dragActivated) e.preventDefault();
      }

      const now = performance.now();
      const pointX = this._getPointX(e);
      const dx = pointX - this._startX;

      // Threshold gate: do nothing that would block clicks until we cross it
      if (!this._dragActivated) {
        if (Math.abs(dx) < this._DRAG_THRESHOLD) {
          return;
        }
        // Crossed threshold → activate dragging
        this._dragActivated = true;
        this._isDragging = true;
        this._track.classList.add('dragging');
        this._movedForClickCancel = true; // real drag -> suppress click on release
        this._btnPrev && (this._btnPrev.disabled = true);
        this._btnNext && (this._btnNext.disabled = true);
        this._stopAutoplay(); // only stop autoplay once actual drag begins
      }

      // From here on, it's an active drag
      if (this._loopActive) {
        this._setTranslateX(this._startTranslateX + dx, false);
      } else {
        // bounded: clamp to [minX, maxX]
        const maxX = this._baseOffsetPx; // will be 0 when centering disabled
        const minX = this._baseOffsetPx - this._maxIndex * this._slideStep;
        const nextX = this._clamp(this._startTranslateX + dx, minX, maxX);
        this._setTranslateX(nextX, false);
      }

      // velocity
      const dt = now - this._lastTs;
      const dxStep = pointX - this._lastPointerX;
      if (dt > 0) this._velocity = dxStep / dt;
      this._lastTs = now;
      this._lastPointerX = pointX;

      this._updateCenterHighlightFromTranslate();
    }

    _onPointerUp(e){
      // Always clean up desktop listeners even if threshold wasn't crossed
      if (this._desktopDragListening) {
        window.removeEventListener('mousemove', this._onPointerMove);
        window.removeEventListener('mouseup', this._onPointerUp);
        this._desktopDragListening = false;
      }

      // If threshold never crossed, treat as a normal click: don't suppress, don't snap
      if (!this._dragActivated) {
        this._isDragging = false;
        this._track.classList.remove('dragging');
        return;
      }

      this._isDragging = false;
      this._dragActivated = false;
      this._track.classList.remove('dragging');

      // if dragged at all (beyond threshold), suppress the synthetic click
      if (this._movedForClickCancel) this._suppressNextClick = true;

      const flick = Math.abs(this._velocity) > 0.4;
      if (flick){
        this._snapToIndex(this._index + (this._velocity < 0 ? 1 : -1), true);
      } else {
        this._snapToNearest();
      }

      setTimeout(() => {
        this._btnPrev && (this._btnPrev.disabled = false);
        this._btnNext && (this._btnNext.disabled = false);
      }, 0);
    }

    _onTouchCancel(){
      if (!this._dragActivated) return;

      this._isDragging = false;
      this._dragActivated = false;
      this._track.classList.remove('dragging');

      // treat as a drag; suppress click if there was movement (there was, threshold crossed)
      this._suppressNextClick = this._movedForClickCancel;

      this._snapToNearest();
      this._btnPrev && (this._btnPrev.disabled = false);
      this._btnNext && (this._btnNext.disabled = false);
    }

    // ===== Buttons =====
    _goPrev(){
      if (!this._isDragging && !this._isAnimating && this._realCount > 0) {
        this._stopAutoplay();
        this._snapToIndex(this._index - 1, true);
      }
    }
    _goNext(){
      if (!this._isDragging && !this._isAnimating && this._realCount > 0) {
        this._stopAutoplay();
        this._snapToIndex(this._index + 1, true);
      }
    }

    // ===== Utils =====
    _getPointX(e){
      if (e && 'clientX' in e) return e.clientX;
      if (e && e.touches && e.touches.length) return e.touches[0].clientX;
      if (e && e.changedTouches && e.changedTouches.length) return e.changedTouches[0].clientX;
      return 0;
    }
    _isTouchEvt(e){ return e && ('touches' in e || 'changedTouches' in e); }
    _clamp(n, min, max){ return Math.min(Math.max(n, min), max); }

    _getBoolAttr(name, fallback = false){
      const v = this.getAttribute(name);
      if (v === null) return fallback;
      if (v === '' || v.toLowerCase() === name) return true;
      return !/^(false|0|off|no)$/i.test(v);
    }
  }
  customElements.define('flex-slideshow', FlexSlideshow);
}
